import {
  Box, Button, Flex, Text
} from '@huspy/briks-web';
import { VStack } from '@huspy/styled-system/jsx';
import { BaseSlido } from '@modules/core/components/BaseSlido';
import { BankApplication } from '@modules/opportunities/entities/bankApplication/bankApplication.entity';
import { useTranslation } from 'react-i18next';

import useSubmitOpportunity from '@modules/opportunities/entities/opportunity/mutation/useSubmitOpportunity';
import { useRef } from 'react';
import { Send } from '@huspy/briks-icons';
import { imagesPath } from '@shared/css.const';
import { Opportunity } from '@modules/opportunities/entities/opportunity/opportunity.entity';
import { SubmissionDetails } from './SubmissionDetails';
import { SuccessMessage } from './SuccessMessage';

const baseTranslation = 'opportunity.caseSubmission.submitApplications.bankAndHuspySubmission.huspySubmissionSlido';

export const BrokerageSubmissionSlido = ({
  bankApplications,
  opportunityId,
  clientExternalId,
  opened,
  close,
  isSubmitAll,
  oppportunity,
}: {
  bankApplications: BankApplication[];
  opportunityId: string;
  clientExternalId: string;
  opened: boolean;
  close: () => void;
  oppportunity: Opportunity;
  isSubmitAll: boolean;
}) => {
  const { t } = useTranslation();

  const { isPending, mutate, isSuccess } = useSubmitOpportunity(
    opportunityId,
    clientExternalId!,
    oppportunity.bank_applications
  );
  const ref = useRef<HTMLTextAreaElement | null>(null);

  const handleSubmit = async () => {
    const additional_comment = ref.current?.value?.length
      ? ref.current.value.trim()
      : null;

    if (isSubmitAll) {
      await mutate({ additional_comment });
    }

    if (!isSubmitAll) {
      try {
        await mutate({
          additional_comment: null,
          bank_applications: [
            {
              external_id: bankApplications[0]?.bank_application_external_id!,
              additional_comment,
            }
          ],
        });
      } catch {
        close();
      }
    }
  };

  const icons = [
    ...bankApplications.map((item) => ({
      id: item.bank_application_external_id,
      icon: item.bank_details.icon,
    })),
    { id: 'huspy-logo', icon: `${imagesPath}/huspy-logo.svg` }
  ];

  return (
    <BaseSlido
      title={ (
        <VStack gap='spacing-1' alignItems='flex-start' p='8'>
          <Text size='3xl' weight='medium'>
            {t(
              `${baseTranslation}.${isSubmitAll ? 'allBanksHeader' : 'singleBankHeader'}`
            )}
          </Text>
        </VStack>
      ) }
      footer={ (
        <Flex w='100%' justify='end' align='end'>
          {isSuccess ? (
            <Button size='md' fontSize='md' onClick={ close } data-test='discard-btn'>
              {t(`${baseTranslation}.done`)}
            </Button>
          ) : (
            <Button
              size='md'
              fontSize='md'
              variant={ isPending ? 'secondary' : 'primary' }
              onClick={ handleSubmit }
              loading={ isPending }
              disabled={ isPending }
              data-test='submit-to-huspy-btn'
            >
              {t(`${baseTranslation}.submit`)}
              <Send color='#fff' />
            </Button>
          )}
        </Flex>
      ) }
      opened={ opened }
      close={ close }
    >
      <Box p='8' h='100%' data-testid='submit-to-huspy'>
        {isSuccess ? (
          <SuccessMessage isSubmittedToBank={ false } />
        ) : (
          <SubmissionDetails
            title={ t(`${baseTranslation}.title`) }
            subtitle={ t(`${baseTranslation}.description`) }
            ref={ ref }
            icons={ icons }
          />
        )}
      </Box>
    </BaseSlido>
  );
};
